import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  Assessment, 
  ComputedResults, 
  DomainScores, 
  DerivedScores, 
  MotivationSignature,
  Archetype,
  ReportNarrative,
  DOMAIN_LABELS,
  CONTRIBUTION_DOMAINS,
  CORE_DOMAINS,
  MOTIVATION_LABELS,
  getScoreBand,
  getScoreBandLabel,
} from '@/lib/assessment-types';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { 
  Loader2, 
  Download, 
  ArrowLeft, 
  User, 
  Target, 
  Heart, 
  Shield,
  TrendingUp,
  AlertTriangle,
  Zap,
  CheckCircle,
  Lightbulb,
  Brain,
  Users,
  Scale,
  Mail,
  Send,
} from 'lucide-react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from 'recharts';

// Score card component
function ScoreCard({ label, score, icon: Icon }: { label: string; score: number; icon: React.ElementType }) {
  const band = getScoreBand(score);
  const bandColor = band === 'high' ? 'text-green-600' : band === 'low' ? 'text-amber-600' : 'text-muted-foreground';
  
  return (
    <div className="card-premium text-center py-4">
      <Icon className="h-6 w-6 text-primary mx-auto mb-2" />
      <p className="text-2xl font-bold text-foreground">{score}%</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={`text-xs font-medium ${bandColor}`}>{getScoreBandLabel(band)}</p>
    </div>
  );
}

const InventoryReport = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { toast } = useToast();
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [results, setResults] = useState<ComputedResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharing, setSharing] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function loadReport() {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      // Check for share token in URL (for shared report access)
      const urlParams = new URLSearchParams(window.location.search);
      const shareToken = urlParams.get('share');

      // If we have a share token, validate it and load the report
      if (shareToken) {
        try {
          // Load assessment data using share token RPC
          const { data: assessmentData, error: assessmentError } = await supabase
            .rpc('get_assessment_by_share_token', {
              p_share_token: shareToken
            });

          if (assessmentError || !assessmentData || assessmentData.length === 0) {
            console.error('Invalid or expired share token:', assessmentError);
            setError('This shared link is invalid or has expired.');
            setLoading(false);
            return;
          }

          setAssessment(assessmentData[0] as Assessment);

          // Load results using share token RPC
          const { data: resultsData, error: resultsError } = await supabase
            .rpc('get_results_by_share_token', {
              p_share_token: shareToken
            });

          if (resultsError || !resultsData || resultsData.length === 0) {
            console.error('Results not found for share token:', resultsError);
            setError('Report data not found.');
            setLoading(false);
            return;
          }

          setResults(resultsData[0] as unknown as ComputedResults);

          // Update accessed_at in background
          supabase
            .from('report_share_tokens')
            .update({ accessed_at: new Date().toISOString() })
            .eq('share_token', shareToken)
            .then(() => console.log('Share token access recorded'));

          setLoading(false);
          return;
        } catch (err) {
          console.error('Share token validation failed:', err);
          setError('Unable to load shared report.');
          setLoading(false);
          return;
        }
      }

      // Get session_secret from localStorage only (never from URL for security)
      const sessionSecret = localStorage.getItem(`assessment_secret_${sessionId}`) 
        || localStorage.getItem('assessment_session_secret')
        || localStorage.getItem(`session_secret_${sessionId}`)  // Legacy fallback
        || localStorage.getItem('inventory_session_secret');  // Legacy fallback

      // If no session secret, check if user is an admin
      if (!sessionSecret) {
        try {
          // Check if user is logged in as admin
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            // Try to load via admin functions
            const { data: assessmentData, error: assessmentError } = await supabase
              .rpc('get_assessment_for_admin', {
                p_assessment_id: sessionId
              });

            if (!assessmentError && assessmentData && assessmentData.length > 0) {
              setAssessment(assessmentData[0] as Assessment);
              setIsAdminView(true);

              const { data: resultsData, error: resultsError } = await supabase
                .rpc('get_assessment_results_for_admin', {
                  p_assessment_id: sessionId
                });

              if (!resultsError && resultsData && resultsData.length > 0) {
                setResults(resultsData[0] as unknown as ComputedResults);
                setLoading(false);
                return;
              }
            }
          }
        } catch (err) {
          console.error('Admin access check failed:', err);
        }

        setError('Session authorization required. Please complete the assessment from the same browser.');
        setLoading(false);
        return;
      }

      try {
        // Load assessment info using secure RPC function
        const { data: assessmentData, error: assessmentError } = await supabase
          .rpc('get_assessment_by_secret', {
            p_assessment_id: sessionId,
            p_session_secret: sessionSecret
          });

        if (assessmentError) throw assessmentError;
        if (!assessmentData || assessmentData.length === 0) {
          throw new Error('Invalid session or authorization');
        }
        
        setAssessment(assessmentData[0] as Assessment);

        // Load results using secure RPC function
        const { data: resultsData, error: resultsError } = await supabase
          .rpc('get_assessment_results_by_secret', {
            p_assessment_id: sessionId,
            p_session_secret: sessionSecret
          });

        if (resultsError) throw resultsError;
        if (!resultsData || resultsData.length === 0) {
          throw new Error('Results not found');
        }

        setResults(resultsData[0] as unknown as ComputedResults);
      } catch (err) {
        console.error('Error loading report:', err);
        setError('Unable to load your report. Please check your authorization.');
      } finally {
        setLoading(false);
      }
    }

    loadReport();
  }, [sessionId]);

  const generatePDF = async () => {
    if (!reportRef.current || !results || !assessment) return;

    setGeneratingPdf(true);

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true, // Enable PDF compression
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const marginTop = 10;
      const marginBottom = 15;
      const marginSide = 10;
      const contentWidth = pdfWidth - (marginSide * 2);
      const maxContentHeight = pdfHeight - marginTop - marginBottom;
      
      // Compression settings - use JPEG with reduced quality for smaller file size
      const imageQuality = 0.75; // 75% quality - good balance of quality vs size
      const canvasScale = 1.5; // Reduced from 2 for smaller file size while maintaining readability
      
      // Get all sections marked for PDF
      const sections = reportRef.current.querySelectorAll('[data-pdf-section]');
      
      if (sections.length === 0) {
        // Fallback: capture entire report
        const canvas = await html2canvas(reportRef.current, {
          scale: canvasScale,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        });

        // Use JPEG instead of PNG for much smaller file size
        const imgData = canvas.toDataURL('image/jpeg', imageQuality);
        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      } else {
        // Section-based rendering with proper page breaks
        let currentY = marginTop;
        let isFirstSection = true;
        const sectionGap = 4;

        for (let i = 0; i < sections.length; i++) {
          const section = sections[i] as HTMLElement;
          
          const canvas = await html2canvas(section, {
            scale: canvasScale,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
          });

          // Use JPEG with compression for each section
          const imgData = canvas.toDataURL('image/jpeg', imageQuality);
          const imgHeight = (canvas.height / canvasScale) * (contentWidth / (canvas.width / canvasScale));
          
          // Check if section fits on current page
          const remainingSpace = maxContentHeight - (currentY - marginTop);
          
          if (!isFirstSection && imgHeight > remainingSpace) {
            pdf.addPage();
            currentY = marginTop;
          }
          
          // Use FAST compression mode for smaller file size
          pdf.addImage(imgData, 'JPEG', marginSide, currentY, contentWidth, imgHeight, undefined, 'FAST');
          currentY += imgHeight + sectionGap;
          isFirstSection = false;
        }
      }

      // Generate filename
      const name = assessment.name?.replace(/\s+/g, '-') || 'Report';
      const date = new Date().toISOString().split('T')[0];
      const filename = `TPL-Report-${name}-${date}.pdf`;

      pdf.save(filename);
    } catch (err) {
      console.error('Error generating PDF:', err);
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Helper function to generate PDF as base64 for sharing
  const generatePDFBase64 = async (): Promise<string | null> => {
    if (!reportRef.current || !results || !assessment) return null;

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const marginTop = 10;
      const marginBottom = 15;
      const marginSide = 10;
      const contentWidth = pdfWidth - (marginSide * 2);
      const maxContentHeight = pdfHeight - marginTop - marginBottom;
      
      const imageQuality = 0.75;
      const canvasScale = 1.5;
      
      const sections = reportRef.current.querySelectorAll('[data-pdf-section]');
      
      if (sections.length === 0) {
        const canvas = await html2canvas(reportRef.current, {
          scale: canvasScale,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        });

        const imgData = canvas.toDataURL('image/jpeg', imageQuality);
        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      } else {
        let currentY = marginTop;
        let isFirstSection = true;
        const sectionGap = 4;

        for (let i = 0; i < sections.length; i++) {
          const section = sections[i] as HTMLElement;
          
          const canvas = await html2canvas(section, {
            scale: canvasScale,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
          });

          const imgData = canvas.toDataURL('image/jpeg', imageQuality);
          const imgHeight = (canvas.height / canvasScale) * (contentWidth / (canvas.width / canvasScale));
          
          const remainingSpace = maxContentHeight - (currentY - marginTop);
          
          if (!isFirstSection && imgHeight > remainingSpace) {
            pdf.addPage();
            currentY = marginTop;
          }
          
          pdf.addImage(imgData, 'JPEG', marginSide, currentY, contentWidth, imgHeight, undefined, 'FAST');
          currentY += imgHeight + sectionGap;
          isFirstSection = false;
        }
      }

      // Return as base64 string
      return pdf.output('datauristring').split(',')[1];
    } catch (err) {
      console.error('Error generating PDF for sharing:', err);
      return null;
    }
  };

  const shareReport = async () => {
    if (!shareEmail || !sessionId || !results) return;

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shareEmail)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    // Get session secret from localStorage
    const sessionSecret = localStorage.getItem(`assessment_secret_${sessionId}`) 
      || localStorage.getItem('assessment_session_secret')
      || localStorage.getItem(`session_secret_${sessionId}`)
      || localStorage.getItem('inventory_session_secret');

    if (!sessionSecret && !isAdminView) {
      toast({
        title: "Authorization required",
        description: "Unable to share report without valid session authorization.",
        variant: "destructive",
      });
      return;
    }

    setSharing(true);

    try {
      toast({
        title: "Preparing report...",
        description: "Generating PDF, this may take a moment.",
      });

      // Generate the PDF on the client
      const pdfBase64 = await generatePDFBase64();

      if (!pdfBase64) {
        throw new Error('Failed to generate PDF');
      }

      const response = await supabase.functions.invoke('share-report', {
        body: {
          recipientEmail: shareEmail,
          sessionId,
          sessionSecret: sessionSecret || '',
          senderName: assessment?.name || 'A colleague',
          pdfBase64, // Send the client-generated PDF
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to share report');
      }

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to share report');
      }

      toast({
        title: "Report shared!",
        description: `Your report has been sent to ${shareEmail}`,
      });
      setShareEmail('');
    } catch (err) {
      console.error('Error sharing report:', err);
      toast({
        title: "Failed to share",
        description: "There was a problem sharing your report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <section className="section-padding min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 text-primary mx-auto mb-4 animate-spin" />
            <p className="text-muted-foreground">Loading your report...</p>
          </div>
        </section>
      </Layout>
    );
  }

  if (error || !results) {
    return (
      <Layout>
        <section className="section-padding min-h-[60vh] flex items-center justify-center">
          <div className="card-premium max-w-md text-center">
            <p className="text-destructive mb-4">{error || 'Report not found'}</p>
            <Link to="/start-inventory">
              <Button>Start New Assessment</Button>
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  // Extract data
  const domainScores = results.domain_scores || {};
  const derivedScores = results.derived_scores || {} as DerivedScores;
  const motivationSignature = results.motivation_signature || {} as MotivationSignature;
  const archetype = results.archetype || {} as Archetype;
  const narrative = results.report_narrative || {} as ReportNarrative;

  // Prepare chart data for core domains (A-H)
  const radarData = CORE_DOMAINS.map(tag => ({
    domain: DOMAIN_LABELS[tag] || tag,
    score: (domainScores as Record<string, number>)[tag] || 0,
    fullMark: 100,
  }));

  // Contribution domains (B-G) for bar chart
  const contributionData = CONTRIBUTION_DOMAINS.map(tag => ({
    name: DOMAIN_LABELS[tag]?.split(' ')[0] || tag,
    score: (domainScores as Record<string, number>)[tag] || 0,
  }));

  // Motivation signature data
  const motivationData = Object.entries(MOTIVATION_LABELS).map(([key, label]) => ({
    name: label.split(' ')[0],
    fullName: label,
    score: (motivationSignature as unknown as Record<string, number>)[key] || 0,
  }));

  // Get top 3 strengths and watch-outs from core domains
  const sortedDomains = CORE_DOMAINS
    .map(tag => ({ tag, label: DOMAIN_LABELS[tag], score: (domainScores as Record<string, number>)[tag] || 0 }))
    .sort((a, b) => b.score - a.score);
  
  const topStrengths = sortedDomains.slice(0, 3);
  const watchOuts = sortedDomains.slice(-3).reverse();

  // Wellbeing note if sustainability strain is high
  const showWellbeingNote = derivedScores.sustainabilityStrain >= 65;

  return (
    <Layout>
      {/* Header */}
      <section className="bg-primary-light py-8 md:py-12">
        <div className="container-wide">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
                Your Leadership Inventory Report
              </h1>
              <p className="text-muted-foreground">
                {assessment?.name} • Completed {new Date(results.computed_at).toLocaleDateString('en-GB')}
              </p>
            </div>
            
            <div className="flex flex-col gap-3">
              {/* Download PDF Button */}
              <Button 
                variant="hero" 
                className="gap-2"
                onClick={generatePDF}
                disabled={generatingPdf}
              >
                {generatingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Download PDF
                  </>
                )}
              </Button>
              
              {/* Share via Email */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="Email to share report"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    className="pl-9 bg-background"
                    onKeyDown={(e) => e.key === 'Enter' && shareReport()}
                  />
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={shareReport}
                  disabled={sharing || !shareEmail}
                  title="Share report via email"
                >
                  {sharing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="section-padding bg-background">
        <div className="container-wide max-w-5xl">
          <div ref={reportRef} className="grid gap-8 bg-background">
            
            {/* Summary Banner */}
            <div data-pdf-section className="card-premium bg-gradient-to-br from-primary-light to-background">
              <div className="flex items-start gap-4">
                <User className="h-8 w-8 text-primary flex-shrink-0" />
                <div className="flex-1">
                  <h2 className="font-display text-xl font-semibold text-foreground mb-3">
                    Your Partner Profile
                  </h2>
                  
                  {/* Archetype badges */}
                  <div className="flex flex-wrap gap-3 mb-4">
                    {archetype.primary && (
                      <span className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground rounded-full font-medium leading-none">
                        {archetype.primary}
                      </span>
                    )}
                    {archetype.secondary && (
                      <span className="inline-flex items-center justify-center px-4 py-2 bg-secondary text-secondary-foreground rounded-full font-medium leading-none">
                        {archetype.secondary}
                      </span>
                    )}
                  </div>
                  
                  {/* Modifiers */}
                  {archetype.modifiers && archetype.modifiers.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {archetype.modifiers.map((mod, i) => (
                        <span key={i} className="inline-flex items-center justify-center px-3 py-1 bg-muted text-muted-foreground text-sm rounded-full leading-none">
                          {mod}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Three-sentence summary */}
                  <div className="space-y-2 text-muted-foreground">
                    <p>
                      <span className="font-medium text-foreground">You most naturally contribute through</span>{' '}
                      {archetype.primary?.toLowerCase() || 'balanced contribution'}, with secondary strength in {archetype.secondary?.toLowerCase() || 'multiple areas'}.
                    </p>
                    <p>
                      <span className="font-medium text-foreground">Under pressure you're most at risk of</span>{' '}
                      {derivedScores.conflictEngagement <= 40 
                        ? 'avoiding necessary conflict and letting issues drift'
                        : derivedScores.sustainabilityStrain >= 65
                        ? 'burnout and boundary erosion'
                        : 'taking on too much without sufficient delegation'
                      }.
                    </p>
                    <p>
                      <span className="font-medium text-foreground">You're likely to stay energised when</span>{' '}
                      {motivationData.sort((a, b) => b.score - a.score)[0]?.fullName.toLowerCase() || 'your core motivators'} are supported.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Wellbeing Note */}
            {showWellbeingNote && (
              <div data-pdf-section className="card-premium border-l-4 border-l-amber-500 bg-amber-50">
                <div className="flex items-start gap-3">
                  <Heart className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground mb-1">A note on wellbeing</p>
                    <p className="text-sm text-muted-foreground">
                      Your responses suggest you may be experiencing some strain. This is common among partners and doesn't reflect on your capability. 
                      Consider discussing workload and boundaries with your partners or seeking support. 
                      <a href="https://www.practitionerhealth.nhs.uk/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                        NHS Practitioner Health
                      </a> offers confidential support.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Derived Scores Cards */}
            <div data-pdf-section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <ScoreCard 
                label="Sustainability Strain" 
                score={derivedScores.sustainabilityStrain || 0}
                icon={Zap}
              />
              <ScoreCard 
                label="Work-Life Strain" 
                score={derivedScores.workLifeStrain || 0}
                icon={Scale}
              />
              <ScoreCard 
                label="Conflict Engagement" 
                score={derivedScores.conflictEngagement || 0}
                icon={Users}
              />
              <ScoreCard 
                label="Values Clarity" 
                score={derivedScores.valuesClarity || 0}
                icon={Target}
              />
            </div>

            {/* Charts Row */}
            <div data-pdf-section className="grid lg:grid-cols-2 gap-6">
              {/* Contribution Domains Bar Chart */}
              <div className="card-premium">
                <h2 className="font-display text-lg font-semibold text-foreground mb-4">
                  Contribution Domains
                </h2>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={contributionData} layout="vertical">
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                        {contributionData.map((entry, index) => (
                          <Cell 
                            key={index} 
                            fill={entry.score >= 65 ? 'hsl(var(--primary))' : entry.score < 45 ? 'hsl(var(--muted))' : 'hsl(var(--secondary))'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Core Domains Radar Chart */}
              <div className="card-premium">
                <h2 className="font-display text-lg font-semibold text-foreground mb-4">
                  Leadership Profile
                </h2>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="hsl(var(--border))" />
                      <PolarAngleAxis 
                        dataKey="domain" 
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 9 }}
                      />
                      <PolarRadiusAxis 
                        angle={30} 
                        domain={[0, 100]}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                      />
                      <Radar
                        name="Score"
                        dataKey="score"
                        stroke="hsl(var(--primary))"
                        fill="hsl(var(--primary))"
                        fillOpacity={0.3}
                        strokeWidth={2}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Narrative: How you contribute */}
            {narrative.contribution && (
              <div data-pdf-section className="card-premium">
                <div className="flex items-start gap-4">
                  <TrendingUp className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="font-display text-lg font-semibold text-foreground mb-2">
                      How You Naturally Contribute as a Partner
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">
                      {narrative.contribution}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Narrative: Under pressure */}
            {narrative.pressure && (
              <div data-pdf-section className="card-premium">
                <div className="flex items-start gap-4">
                  <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="font-display text-lg font-semibold text-foreground mb-2">
                      How You Lead Under Pressure
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">
                      {narrative.pressure}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Strengths and Watch-outs */}
            <div data-pdf-section className="grid md:grid-cols-2 gap-6">
              {/* Strengths */}
              <div className="card-premium">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <h2 className="font-display text-lg font-semibold text-foreground">
                    Your Strengths
                  </h2>
                </div>
                <ul className="space-y-3">
                  {topStrengths.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="h-2 w-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-foreground">{item.label}</span>
                        <span className="text-muted-foreground ml-2">({item.score}%)</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Watch-outs */}
              <div className="card-premium">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                  <h2 className="font-display text-lg font-semibold text-foreground">
                    Watch-outs
                  </h2>
                </div>
                <ul className="space-y-3">
                  {watchOuts.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="h-2 w-2 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-foreground">{item.label}</span>
                        <span className="text-muted-foreground ml-2">({item.score}%)</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Motivation Signature */}
            <div data-pdf-section className="card-premium">
              <div className="flex items-start gap-4">
                <Heart className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h2 className="font-display text-lg font-semibold text-foreground mb-2">
                    Your Motivation Signature
                  </h2>
                  {narrative.motivation && (
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      {narrative.motivation}
                    </p>
                  )}
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={motivationData} layout="vertical">
                        <XAxis type="number" domain={[0, 100]} />
                        <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(value) => `${value}%`} />
                        <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                          {motivationData.map((entry, index) => (
                            <Cell 
                              key={index} 
                              fill={entry.score >= 65 ? 'hsl(var(--primary))' : 'hsl(var(--secondary))'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>

            {/* Values narrative */}
            {narrative.values && (
              <div data-pdf-section className="card-premium">
                <div className="flex items-start gap-4">
                  <Brain className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="font-display text-lg font-semibold text-foreground mb-2">
                      Your Values and What You Optimise For
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">
                      {narrative.values}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Sustainability narrative */}
            {narrative.sustainability && (
              <div data-pdf-section className="card-premium">
                <div className="flex items-start gap-4">
                  <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="font-display text-lg font-semibold text-foreground mb-2">
                      Sustainability and Boundaries
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">
                      {narrative.sustainability}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Reflection Prompts */}
            <div data-pdf-section className="card-premium">
              <div className="flex items-start gap-4">
                <Lightbulb className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-lg font-semibold text-foreground mb-4">
                    Reflection Prompts
                  </h2>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <span className="h-6 w-6 rounded-full bg-primary-light text-primary flex items-center justify-center text-sm font-medium flex-shrink-0">1</span>
                      <span className="text-muted-foreground">
                        Looking at your primary contribution style ({archetype.primary}), what recent example demonstrates this in action?
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-6 w-6 rounded-full bg-primary-light text-primary flex items-center justify-center text-sm font-medium flex-shrink-0">2</span>
                      <span className="text-muted-foreground">
                        When did you last experience tension between your values and a partnership decision? How did you handle it?
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-6 w-6 rounded-full bg-primary-light text-primary flex items-center justify-center text-sm font-medium flex-shrink-0">3</span>
                      <span className="text-muted-foreground">
                        What one boundary would most protect your sustainability over the next 6 months?
                      </span>
                    </li>
                    {derivedScores.conflictEngagement <= 40 && (
                      <li className="flex items-start gap-3">
                        <span className="h-6 w-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-sm font-medium flex-shrink-0">4</span>
                        <span className="text-muted-foreground">
                          What conversation have you been avoiding? What would help you raise it constructively?
                        </span>
                      </li>
                    )}
                    {derivedScores.sustainabilityStrain >= 65 && (
                      <li className="flex items-start gap-3">
                        <span className="h-6 w-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-sm font-medium flex-shrink-0">5</span>
                        <span className="text-muted-foreground">
                          What would need to change for your workload to feel sustainable? Who could you discuss this with?
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div data-pdf-section className="text-center text-sm text-muted-foreground pt-6 border-t border-border">
              <p>
                This report is for developmental purposes and self-reflection. It should not be used in isolation for hiring or performance decisions.
              </p>
              <p className="mt-2">
                © {new Date().getFullYear()} The Partnership Lab •{' '}
                <a href="/legal#privacy" className="text-primary hover:underline">Privacy Policy</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default InventoryReport;
